import React, { useEffect, useState } from 'react';
import ButtonPrimary from '../../../../../Common/Button/Primary/ButtonPrimary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Input from '../../../../../Common/Input/Input';
import styles from './SignUpConfirmStep.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import StepTitle from '../../../../Features/StepTitle/StepTitle';
import StepDescription from '../../../../Features/StepDescription/StepDescription';
import { useAppDispatch, useAppSelector } from '../../../../../../config/redux/hooks';
import { confirmCode, resendCode } from './signupConfirmSlice';
import { useNotification } from '../../../../../Common/Notification/notify';
import useNotifyOnError from '../../../../../../common/form/useNotifyOnError';

function SignUpConfirmStep() {
  const [code, setCode] = useState('');
  const { t } = useTranslation();
  const notify = useNotification();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const email = useAppSelector(state => state.auth.signup.main.email);
  const readyToLogin = useAppSelector(state => state.auth.signup.confirm.readyToLogin);
  const error = useAppSelector(state => state.auth.signup.confirm.error);
  const resendCodeSuccessful = useAppSelector(state => state.auth.signup.confirm.resendCodeSuccessful);

  useEffect(() => {
    if (readyToLogin) {
      navigation('/auth/signup/success');
    }
  }, [readyToLogin]);

  useNotifyOnError(error);

  useEffect(() => {
    if (resendCodeSuccessful) {
      notify.success(t('signup.confirm.notification.resend'));
    }
  }, [resendCodeSuccessful]);

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .transform(it => it.replace(/\s|-+/g, ''))
        .matches(/^(?=.*[0-9]$).{6}$/, t('common.form.validation.code'))
        .required(t('common.form.validation.required')),
    }),
    onSubmit: values => {
      const code = values.code.replaceAll(' ', '').replaceAll('-', '');
      dispatch(confirmCode({ email, code }));
    },
  });

  const changeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s|-+/g, '');
    const newValue = [...value]
      .map((char, index) => {
        // adding whitespace every 2 letters except last one
        // why? I didn't find easy way to add gaps inside input like in design!,
        // so that is hacky solution to add whitespaces between 'elements'
        return value.length !== index + 1 && (index + 1) % 2 === 0 ? char + ' ' : char;
      })
      .join('');
    setCode(newValue);
    formik.handleChange(e);
  };

  const resendCodeClick = () => {
    dispatch(resendCode(email));
  };

  return (
    <div className={styles.container}>
      <StepTitle>{t('signup.confirm.title')}</StepTitle>
      <StepDescription>
        {t('signup.confirm.description')}
        <a href={`https://gmail.com`} target='_blank' rel='noreferrer' className={styles.colored}>
          {email}
        </a>
      </StepDescription>
      <form className={styles.form} onSubmit={formik.handleSubmit}>
        <Input
          wrapperClassName={styles.inputWrapper}
          className={styles.input}
          placeholder={t('signup.confirm.code.placeholder')}
          id={'code'}
          name={'code'}
          type={'string'}
          maxLength={8}
          onChange={changeInput}
          onBlur={formik.handleBlur}
          value={code}
          touched={formik.touched.code}
          error={formik.errors.code}
          errorBoundary
          messageClassName={styles.inputError}
        />
        <div className={styles.actions}>
          <div className={styles.action} onClick={() => resendCodeClick()}>
            {t('signup.confirm.action.resend')}
          </div>
        </div>
        <ButtonPrimary type={'submit'} disabled={!formik.values.code || !formik.isValid} label={t('signup.confirm.button.confirm')} />
      </form>
    </div>
  );
}

export default SignUpConfirmStep;
